// JIRA: LOGISPRD-14929
import type { StockLedgerFilter, StockLedgerView } from '@/services/inventory/stockLedger/types'

/** 마지막으로 본 조회 — 조건·조회 순번·페이지(`StockLedgerView`). 조건·순번이 목록 query key 다 (LOGISPRD-15270). */
export type StockLedgerLastView = StockLedgerView

/**
 * 마지막으로 본 조회를 **화면 밖(모듈)에 둔다**.
 *
 * 다른 메뉴 탭에 다녀오면 이 화면은 언마운트됐다 다시 마운트된다. 마운트 때 남겨 둔 조건(URL)으로 돌아왔으면 조회 순번을
 * 되돌려 같은 query key 를 만들고 보던 페이지도 되살린다 — 캐시가 남아 있으면 다시 받지 않고 보던 페이지를 바로 보여 준다(LOGISPRD-14439 가 넣은
 * 진입 조회는 캐시가 없을 때만 돈다). 브라우저 새로고침이면 모듈과 캐시가 함께 사라진다.
 */
let lastView: StockLedgerLastView | undefined

/**
 * 조회 순번. **이것도 모듈에 둔다** — 화면 안에 두면 재진입 때 0 부터 다시 세어, 재진입 뒤 첫 [조회]가 예전 조회와 같은 순번을 받는다.
 * 조건까지 같으면 key 가 같아 [조회]를 눌렀는데 캐시가 나온다.
 */
let searchSeq = 0

export const nextSearchSeq = (): number => {
  searchSeq += 1
  return searchSeq
}

export const getLastView = (): StockLedgerLastView | undefined => lastView

export const saveLastView = (view: StockLedgerLastView): void => {
  lastView = view
}

export const clearLastView = (): void => {
  lastView = undefined
}

/** 두 조건이 서버에 같은 요청을 보내는가. 필드별로 비교한다 — 키 순서·빈 선택값(undefined/'')의 차이로 다르게 판정하지 않는다. */
const isSameFilter = (a: StockLedgerFilter, b: StockLedgerFilter): boolean =>
  a.SPR_NM === b.SPR_NM &&
  a.CLS_DATE[0] === b.CLS_DATE[0] &&
  a.CLS_DATE[1] === b.CLS_DATE[1] &&
  (a.BRD_NM ?? '') === (b.BRD_NM ?? '') &&
  (a.GDS_CD ?? '') === (b.GDS_CD ?? '') &&
  (a.WH_NM ?? '') === (b.WH_NM ?? '')

/**
 * 진입 조건으로 시작할 조회. 마지막 조회와 **같은 조건일 때만** 그 순번·페이지를 되살린다 — 다른 조건(URL 로 다른 화주를 열었을 때 등)이면
 * 새 순번·1페이지로 새로 조회한다. 조건이 없으면(첫 진입) 조회하지 않는다.
 *
 * 새 조회일 때 `nextSearchSeq()` 로 순번을 올리는 부수 효과가 있다 — 화면 마운트 때 `useState` 초기화 함수에서 한 번만 부른다
 * (앱은 StrictMode 를 쓰지 않는다). 다른 곳에서 반복 호출하지 않는다.
 */
export const startingView = (filter: StockLedgerFilter | null): StockLedgerLastView | null => {
  if (!filter) return null
  const last = getLastView()
  return last && isSameFilter(last.filter, filter) ? last : { filter, seq: nextSearchSeq(), page: 1 }
}

/** 테스트 전용 — 모듈 상태를 비운다. */
export const __resetLastViewForTest = (): void => {
  lastView = undefined
  searchSeq = 0
}
